// src/types/stage.ts
//
// Mirror of MailSendApi/Models/EmailStage.cs. Keep the two in step.
//
// Stage is what an operator actually wants to know: where is this email in its
// life. SmtpStatus and DeliveryStatus each answer half the question, and asking
// people to combine them in their head is why the old dashboard was hard to
// read during a bulk run.

export const EMAIL_STAGES = [
  'Queued',
  'Sending',
  'InTransit',
  'Delivered',
  'Bounced',
  'Failed',
  'Cancelled',
  'Archived',
] as const;

export type EmailStage = (typeof EMAIL_STAGES)[number];

/** Live work. The Queue view. */
export const ACTIVE_STAGES: EmailStage[] = ['Queued', 'Sending', 'InTransit'];

/** Finished, one way or another. The History view. */
export const TERMINAL_STAGES: EmailStage[] = ['Delivered', 'Bounced', 'Failed', 'Cancelled'];

export type StageTone = 'neutral' | 'progress' | 'good' | 'warn' | 'bad';

export interface StageDisplay {
  label: string;
  tone: StageTone;
  description: string;
}

export const STAGE_DISPLAY: Record<EmailStage, StageDisplay> = {
  Queued: {
    label: 'Queued',
    tone: 'neutral',
    description: 'Accepted for sending, waiting for the queue processor.',
  },
  Sending: {
    label: 'Sending',
    tone: 'progress',
    description: 'Claimed by a batch or being transmitted right now.',
  },
  InTransit: {
    // "In transit" suggests the mail is still moving. It is not -- the receiving
    // server has taken it, and we are only waiting to see whether it bounces.
    label: 'Sent',
    tone: 'progress',
    description: 'The mail server accepted it. Waiting to see whether it bounces.',
  },
  Delivered: {
    // Stage-level label only. Per email the badge is more precise, because this
    // stage covers both a real delivery report and the absence of a bounce --
    // see describeStage.
    label: 'Delivered',
    tone: 'good',
    description: 'Delivered, or no bounce arrived inside the delivery window.',
  },
  Bounced: {
    label: 'Bounced',
    tone: 'bad',
    description: 'The receiving server rejected it after accepting it.',
  },
  Failed: {
    label: 'Failed',
    tone: 'bad',
    description: 'Never left the building — refused, or out of retries.',
  },
  Cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    description: 'Withdrawn before it was sent.',
  },
  Archived: {
    label: 'Archived',
    tone: 'neutral',
    description: 'Closed and moved out of the working views.',
  },
};

export const STAGE_TONE_CLASS: Record<StageTone, string> = {
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
  progress: 'bg-blue-100 text-blue-700 border-blue-200',
  good: 'bg-green-100 text-green-700 border-green-200',
  warn: 'bg-amber-100 text-amber-800 border-amber-200',
  bad: 'bg-red-100 text-red-700 border-red-200',
};

/**
 * How to label one email.
 *
 * Stage alone cannot separate the two ways an email reaches Delivered: a real
 * delivery report said so, or no bounce turned up before the window closed.
 * Those are different claims and worth showing differently -- SMTP gives no
 * positive delivery receipt, so "no bounce" is an inference, not proof.
 *
 * Pass deliveryStatus to get the precise label. Without it the caller gets the
 * stage-level one, which is what the filter chips want.
 */
export function describeStage(stage: string, deliveryStatus?: string): StageDisplay {
  const base = STAGE_DISPLAY[stage as EmailStage] ?? {
    label: stage,
    tone: 'neutral' as StageTone,
    description: `Unrecognised stage "${stage}".`,
  };

  if (stage === 'Delivered' && deliveryStatus === 'LikelyDelivered') {
    return {
      ...base,
      label: 'Likely delivered',
      description:
        'No bounce arrived inside the delivery window, so it almost certainly landed. ' +
        'Nothing confirmed it outright.',
    };
  }

  if (stage === 'Delivered' && deliveryStatus === 'Delivered') {
    return {
      ...base,
      label: 'Delivered',
      description: 'A delivery report confirmed it arrived.',
    };
  }

  return base;
}

/** Counts behind the stage chips. Every stage is always present, even at zero. */
export interface StageCounts {
  stages: Record<EmailStage, number>;
  /** Queued but not eligible yet: paused, scheduled, or inside a retry backoff. */
  held: number;
  /** Queued and eligible right now. */
  ready: number;
  active: number;
  total: number;
}

export interface CampaignSummary {
  campaign: string;
  total: number;
  firstQueuedAt: string;
  lastQueuedAt: string;
  queued: number;
  sending: number;
  inTransit: number;
  delivered: number;
  bounced: number;
  failed: number;
  cancelled: number;
  archived: number;
}
